import { isFulfilled } from "@reduxjs/toolkit";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../app/hooks";
import BasicDialog from "../../../../common/components/functional/dialogs/BasicDialog";
import useMessage from "../../../../hooks/useMessage";
import { Entry } from "../../entriesAPI";
import { addNewEntries } from "../../entriesSlice";
import { AddEntry } from "../../types/commands";
import { CopyEntriesForm } from "./CopyEntriesForm";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useIsLoading from "../../../../hooks/useIsLoading";

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  entries: Entry[] | null;
};

export const CopyEntries = ({ isOpen, closeModal, entries }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { sendMessage } = useMessage();
  const isLoading = useIsLoading(["entries.entries"]);

  const defaultValues = {
    startDate: null,
    endDate: null,
    weekdays: [1, 2, 3, 4, 5],
  };
  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { getValues } = methods;

  const createTimeRange = (start: Date, end: Date) => {
    const range = [];
    let currentDate = start;
    while (currentDate <= end) {
      range.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    const filteredDates = range.filter((date) =>
      getValues().weekdays.includes(date.getDay()),
    );
    return filteredDates;
  };

  const handleNewEntryTimes = (newDate: Date, entry: Entry) => {
    const entryStart = new Date(entry.start);
    entryStart.setDate(newDate.getDate());
    entryStart.setMonth(newDate.getMonth());
    entryStart.setFullYear(newDate.getFullYear());
    const entryLength = entry.end - entry.start;
    const entryEnd = entryStart.getTime() + entryLength;

    return { start: entryStart.getTime(), end: entryEnd };
  };

  const handleNewLunchStartTime = (newDate: Date, entry: Entry) => {
    if (entry.lunchBreak === null) return null;
    const lunchStart = new Date(entry.lunchBreak?.start);
    lunchStart.setDate(newDate.getDate());
    lunchStart.setMonth(newDate.getMonth());
    lunchStart.setFullYear(newDate.getFullYear());
    return lunchStart.getTime();
  };

  const handleSupplementsTime = (newDate: Date, entry: Entry) => {
    if (!entry.supplements) return [];
    return entry.supplements?.map((supplement) => {
      if (supplement.time) {
        const supplementStart = new Date(supplement.time.start);
        supplementStart.setDate(newDate.getDate());
        supplementStart.setMonth(newDate.getMonth());
        supplementStart.setFullYear(newDate.getFullYear());
        const supplementLength = supplement.time.end - supplement.time.start;
        const supplementEnd = supplementStart.getTime() + supplementLength;
        return {
          ...supplement,
          time: { start: supplementStart.getTime(), end: supplementEnd },
        };
      } else return supplement;
    });
  };

  const createEntries = () => {
    if (!entries) return;
    const { startDate, endDate } = getValues();
    const timeRange =
      !!startDate && !!endDate ? createTimeRange(startDate, endDate) : [];
    let newEntries: AddEntry[] = [];
    timeRange.forEach((date) => {
      entries.forEach((entry) => {
        const newEntry = handleNewEntryTimes(date, entry);
        const lunchStart = handleNewLunchStartTime(date, entry);
        const updatedSupplements = handleSupplementsTime(date, entry);
        newEntries.push({
          assignment: entry.assignmentId!!,
          isBillable: entry.isBillable || false,
          markApproved: false,
          details: {
            type: "WORK_ENTRY",
            start: newEntry.start,
            end: newEntry.end,
            customerCostCenterId: entry.customerCostCenterId,
            supplements: updatedSupplements,
            lunchBreak: entry.lunchBreak,
            ...(entry.lunchBreak &&
              lunchStart && {
                lunchBreak: { ...entry.lunchBreak, start: lunchStart },
              }),
            maxHoursToBank: entry.maxHoursToBank ?? null,
            flexibleHours: entry.flexibleHours ?? null,
          },
        });
      });
    });
    // TODO maybe limit the number of entries to be created
    dispatch(addNewEntries(newEntries)).then((action) => {
      if (isFulfilled(action)) {
        sendMessage(t("hourEntries:copySuccess"), "success");
        closeModal();
      }
    });
  };

  return (
    <BasicDialog
      open={isOpen}
      onCancel={closeModal}
      onConfirm={createEntries}
      title={t("hourEntries:copyEntries")}
      confirmLabel={t("common:copy")}
      PaperProps={{ sx: { minWidth: "572px" } }}
      confirmDisabled={false}
      noBorder
      content={
        <>
          <OverlaySpinner isLoading={isLoading} />
          <FormProvider {...methods}>
            <CopyEntriesForm />
          </FormProvider>
        </>
      }
    />
  );
};
